import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import { useVirtualizer } from '@tanstack/react-virtual';
import { getCustomerOrderDetail } from '../../api';
import { handleError, checkStatus, parseJSON } from '../../../shared/api/core/common';
import { mapItemDetails, getStatusTextForQty } from '../../lib/orderPickingItems';

interface Props {
  orderId: string | number;
  businessId: number | string;
  locationId: string;
}

const OrderExpandRowContent = (props: Props) => {
  const { orderId, businessId, locationId } = props;
  const [loading, setLoading] = useState(true);
  const [orderData, setOrderData] = useState<any>(null);
  const [rows, setRows] = useState<any[]>([]);
  const parentRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLoading(true);
    getCustomerOrderDetail(orderId, businessId, locationId)
      .catch(handleError)
      .then(checkStatus)
      .then(parseJSON)
      .then((res: any) => {
        const od = res.order_data;
        setOrderData(od);
        const items = (od.items || [])
          .filter((i: any) => !i.is_deleted)
          .map((i: any) => mapItemDetails(i));
        setRows(items);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [orderId, businessId, locationId]);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 44,
    overscan: 6,
  });

  if (loading) {
    return (
      <Box p={2} display="flex" justifyContent="center">
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!orderData) {
    return (
      <Box p={2}>
        <Typography variant="body2">Could not load order detail.</Typography>
      </Box>
    );
  }

  return (
    <Box p={1} style={{ maxWidth: '100%', overflow: 'hidden' }}>
      <Typography variant="body2" gutterBottom noWrap title={String(orderData.order_number)}>
        Net: {orderData.net_total != null ? String(orderData.net_total) : '—'} · Lines: {rows.length}
      </Typography>
      <Box
        display="flex"
        fontSize={12}
        fontWeight="bold"
        borderBottom="1px solid #ccc"
        pb={0.5}
        mb={0.5}
      >
        <Box flex="1 1 28%" style={{ wordBreak: 'break-all' }}>
          Product
        </Box>
        <Box width={40} textAlign="right">
          Ord
        </Box>
        <Box width={44} textAlign="right">
          Pck
        </Box>
        <Box width={36} textAlign="center">
          Done
        </Box>
        <Box width={52} textAlign="right">
          Line
        </Box>
        <Box flex="0 0 64px">St</Box>
      </Box>
      <div
        ref={parentRef}
        style={{
          maxHeight: 260,
          overflow: 'auto',
        }}
      >
        <Box height={virtualizer.getTotalSize()} position="relative" width="100%">
          {virtualizer.getVirtualItems().map((vRow) => {
            const item = rows[vRow.index];
            const line =
              item.net_total != null ? Number(item.net_total).toFixed(2) : '—';
            return (
              <Box
                key={vRow.index}
                position="absolute"
                top={0}
                left={0}
                width="100%"
                height={vRow.size}
                style={{
                  transform: `translateY(${vRow.start}px)`,
                }}
                display="flex"
                alignItems="center"
                borderBottom="1px solid #eee"
                fontSize={12}
                px={0.5}
              >
                <Typography variant="caption" style={{ flex: '1 1 28%', wordBreak: 'break-word' }}>
                  {item.product?.name}
                </Typography>
                <Typography variant="caption" style={{ width: 40, textAlign: 'right' }}>
                  {item.quantity}
                </Typography>
                <Typography variant="caption" style={{ width: 44, textAlign: 'right' }}>
                  {item.pickedQty}
                </Typography>
                <Typography variant="caption" style={{ width: 36, textAlign: 'center' }}>
                  {item.confirmed ? 'Y' : 'N'}
                </Typography>
                <Typography variant="caption" style={{ width: 52, textAlign: 'right' }}>
                  {line}
                </Typography>
                <Typography variant="caption" style={{ width: 64 }}>
                  {getStatusTextForQty(item)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </div>
    </Box>
  );
};

export default OrderExpandRowContent;
